import React from 'react';
import { 
  Users, 
  Gamepad2, 
  DollarSign, 
  TrendingUp,
  Crown,
  Activity
} from 'lucide-react';

const AdminStatsCards = ({ systemStats, isLoading }) => {
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="card animate-pulse">
            <div className="flex items-center">
              <div className="p-3 bg-discord-dark rounded-full">
                <div className="h-6 w-6 bg-discord-dark rounded"></div>
              </div>
              <div className="ml-4">
                <div className="h-4 bg-discord-dark rounded w-20 mb-2"></div>
                <div className="h-8 bg-discord-dark rounded w-16"></div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  const stats = [
    {
      title: 'Total Users',
      value: systemStats?.users?.total || 0,
      icon: Users,
      color: 'discord-blurple',
      change: systemStats?.users?.newToday || 0,
      changeLabel: 'new today'
    },
    {
      title: 'Total Games',
      value: systemStats?.games?.total || 0,
      icon: Gamepad2,
      color: 'discord-green',
      change: systemStats?.games?.today || 0,
      changeLabel: 'today'
    },
    {
      title: 'Conversion Rate',
      value: `${systemStats?.revenue?.conversionRate || 0}%`,
      icon: DollarSign,
      color: 'discord-yellow',
      change: null
    },
    {
      title: 'Monthly Growth',
      value: systemStats?.users?.newThisMonth || 0,
      icon: TrendingUp,
      color: 'discord-fuchsia',
      change: null
    }
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      {stats.map((stat, index) => {
        const Icon = stat.icon;
        return (
          <div key={index} className="card">
            <div className="flex items-center">
              <div className={`p-3 bg-${stat.color}/20 rounded-full`}>
                <Icon className={`h-6 w-6 text-${stat.color}`} />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-discord-light">
                  {stat.title}
                </p>
                <p className="text-2xl font-bold text-discord-lightest">
                  {stat.value}
                </p>
                {stat.change !== null && (
                  <p className="text-xs text-discord-light">
                    +{stat.change} {stat.changeLabel}
                  </p>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const SubscriptionBreakdown = ({ subscriptionStats }) => {
  if (!subscriptionStats || subscriptionStats.length === 0) {
    return null;
  }

  const totalUsers = subscriptionStats.reduce((sum, stat) => sum + stat.count, 0);

  return (
    <div className="card">
      <h2 className="text-xl font-bold text-discord-lightest mb-4">Subscription Breakdown</h2>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {subscriptionStats.map((stat) => {
          const percentage = totalUsers > 0 ? Math.round((stat.count / totalUsers) * 100) : 0;
          return (
            <div key={stat._id} className="p-4 bg-discord-dark rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <p className="text-sm text-discord-light capitalize">
                    {stat._id === 'free' ? 'Free' : stat._id}
                  </p>
                  <p className="text-2xl font-bold text-discord-lightest">
                    {stat.count}
                  </p>
                  <p className="text-xs text-discord-light">
                    {percentage}% of total
                  </p>
                </div>
                <Crown className="h-6 w-6 text-discord-yellow" />
              </div>
              <div className="w-full bg-discord-darker rounded-full h-2">
                <div 
                  className="bg-discord-yellow h-2 rounded-full transition-all duration-300"
                  style={{ width: `${percentage}%` }}
                ></div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const SystemHealth = ({ systemStats }) => {
  const healthMetrics = [
    {
      label: 'Database',
      status: 'healthy',
      color: 'discord-green'
    },
    {
      label: 'Discord Bot',
      status: 'online',
      color: 'discord-green'
    },
    {
      label: 'API',
      status: 'operational',
      color: 'discord-green'
    },
    {
      label: 'Payments',
      status: 'active',
      color: 'discord-green'
    }
  ];

  return (
    <div className="card">
      <h2 className="text-xl font-bold text-discord-lightest mb-4">System Health</h2>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {healthMetrics.map((metric, index) => (
          <div key={index} className="text-center">
            <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-${metric.color}/20 text-${metric.color} mb-2`}>
              <Activity className="h-3 w-3 mr-1" />
              {metric.status}
            </div>
            <p className="text-sm text-discord-light">{metric.label}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

export { AdminStatsCards, SubscriptionBreakdown, SystemHealth };
